import { useState, useEffect } from 'react';
import { UserX, UserCheck, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { getGlobalSettings, toggleRegistration, toggleSignupApproval } from 'wasp/client/operations';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

type RegistrationSettings = {
  enabled: boolean;
  message: string;
};

const RegistrationTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [signupApprovalRequired, setSignupApprovalRequired] = useState(true);
  const [message, setMessage] = useState(
    'Registrations are currently closed. Please check back later.'
  );

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getGlobalSettings();
      const regSetting = settings.find((s: any) => s.settingKey === 'registration_open');

      if (regSetting) {
        const value = regSetting.settingValue as RegistrationSettings;
        setEnabled(value.enabled ?? true);
        setMessage(
          value.message || 'Registrations are currently closed. Please check back later.'
        );
      }

      const approvalSetting = settings.find(
        (s: any) => s.settingKey === 'signup_requires_approval'
      );
      if (approvalSetting) {
        const value = approvalSetting.settingValue as { enabled?: boolean };
        setSignupApprovalRequired(value.enabled ?? true);
      } else {
        setSignupApprovalRequired(true);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load registration settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await toggleRegistration({ enabled, message });
      toast.success(
        enabled ? 'Registrations are now open' : 'Registrations have been closed'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to update registration settings');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    try {
      setSaving(true);
      await toggleRegistration({ enabled: newEnabled, message });
      toast.success(newEnabled ? 'Registrations are now open' : 'Registrations have been closed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle registration');
      setEnabled(!newEnabled);
    } finally {
      setSaving(false);
    }
  };

  const handleSignupApprovalToggle = async () => {
    const newRequired = !signupApprovalRequired;
    setSignupApprovalRequired(newRequired);
    try {
      setSaving(true);
      await toggleSignupApproval({ enabled: newRequired });
      toast.success(
        newRequired
          ? 'New signups now require admin approval'
          : 'New signups are approved automatically'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle signup approval');
      setSignupApprovalRequired(!newRequired);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Loading registration settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-6 py-6 space-y-6">
        {/* Status Alert */}
        {!enabled && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Registrations are Closed</AlertTitle>
            <AlertDescription>
              New users cannot sign up. Admin and platform owner accounts are not affected.
            </AlertDescription>
          </Alert>
        )}

        {/* Quick Toggle */}
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5" />
              <h3 className="font-semibold leading-none tracking-tight">Allow New Registrations</h3>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleQuickToggle}
              disabled={saving}
              className="ml-auto"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            When disabled, no new users can sign up via any method (email, Google, etc.)
          </p>
        </div>

        {/* Signup approval */}
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              <h3 className="font-semibold leading-none tracking-tight">
                Require Admin Approval for New Signups
              </h3>
            </div>
            <Switch
              checked={signupApprovalRequired}
              onCheckedChange={handleSignupApprovalToggle}
              disabled={saving}
              className="ml-auto"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            When off, new users can sign up and log in immediately without waiting for approval.
            Admin and platform owner emails are always auto-approved.
          </p>
        </div>

        {/* Closed Message */}
        <div className="space-y-2">
          <Label htmlFor="reg-message">Message Shown to Users When Closed</Label>
          <Textarea
            id="reg-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter the message users will see when registration is closed..."
            rows={4}
            className="resize-none"
          />
          <p className="text-sm text-muted-foreground">
            This message appears on the signup page and is returned when signup is attempted
          </p>
        </div>

        {/* Save */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={loadSettings} disabled={saving}>
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Info */}
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">Important Notes</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>Admin and Platform Owner emails</strong> (configured in environment variables)
                can always sign up regardless of this setting
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>Existing users</strong> are not affected — they can still log in normally
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>All signup methods</strong> are blocked: email/password, Google, GitHub, and Discord
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>
                <strong>Signup approval</strong> only affects new accounts. Existing pending users are not
                auto-approved when you turn approval off.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RegistrationTab;



